import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Send, ArrowRight, BarChart3, Boxes, ReceiptText, RefreshCcw, AlertTriangle, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useAuth, useWorkspace } from "@/lib/store";
import { fetchAssistantMessages, fetchAssistantReply } from "@/lib/shopApi";
import { notifyInsufficientCredits } from "@/lib/apiClient";
import { languageOptions, useLocalization } from "@/lib/localization";

const ASSISTANT_MESSAGE_CONTEXT_LIMIT = 4;
const ASSISTANT_LOCAL_PRODUCTS_LIMIT = 10;
const ASSISTANT_LOCAL_RECENT_SALES_LIMIT = 6;
const ASSISTANT_MESSAGE_COST_UNITS = 500;

function sanitizeAssistantText(value) {
  return String(value || "")
    .replace(/`+/g, "")
    .replace(/\*+/g, "")
    .replace(/_+/g, "")
    .replace(/#+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function selectAssistantLocalProducts(products) {
  return [...products]
    .sort((left, right) => {
      const leftLowStock = left.stock !== null && left.stock <= left.lowStockAt ? 1 : 0;
      const rightLowStock = right.stock !== null && right.stock <= right.lowStockAt ? 1 : 0;

      if (leftLowStock !== rightLowStock) {
        return rightLowStock - leftLowStock;
      }

      if ((left.soldToday ?? 0) !== (right.soldToday ?? 0)) {
        return (right.soldToday ?? 0) - (left.soldToday ?? 0);
      }

      const leftUnknownStock = left.stock === null ? 1 : 0;
      const rightUnknownStock = right.stock === null ? 1 : 0;
      if (leftUnknownStock !== rightUnknownStock) {
        return rightUnknownStock - leftUnknownStock;
      }

      return String(left.name || "").localeCompare(String(right.name || ""));
    })
    .slice(0, ASSISTANT_LOCAL_PRODUCTS_LIMIT)
    .map((product) => ({
      cost: product.cost,
      id: product.id,
      lowStockAt: product.lowStockAt,
      name: product.name,
      price: product.price,
      status: product.status ?? "Active",
      stock: product.stock,
      stockTrackingEnabled: product.stockTrackingEnabled ?? product.stock !== null,
    }));
}

function selectAssistantRecentSales(sales, common) {
  return sales.slice(0, ASSISTANT_LOCAL_RECENT_SALES_LIMIT).map((sale) => ({
    amountPaid: sale.amountPaid ?? 0,
    balanceDue: sale.balanceDue ?? Math.max(0, (sale.total ?? 0) - (sale.amountPaid ?? 0)),
    cashier: sale.cashier ?? common.unknown,
    createdAtIso: sale.createdAt ?? sale.createdAtIso ?? null,
    debtId: sale.debtId ?? null,
    id: sale.id,
    items: (sale.items ?? []).map((item) => ({
      cartId: item.cartId ?? item.productId ?? item.name,
      name: item.name,
      productId: item.productId,
      quantity: item.quantity ?? 1,
      stock: item.stock ?? null,
      unitPrice: item.unitPrice ?? 0,
    })),
    paymentStatus: sale.paymentStatus ?? "Paid",
    total: sale.total ?? 0,
    updatedAt: sale.createdAt ?? sale.createdAtIso ?? new Date().toISOString(),
  }));
}

function extractAssistantReply(response) {
  return response?.reply || response?.assistant?.reply || null;
}

function toAssistantSyncSummary(sync) {
  if (!sync) {
    return undefined;
  }

  return {
    failedCount: sync.failedCount ?? 0,
    isProcessing: Boolean(sync.isProcessing),
    lastError: sync.lastError ?? null,
    lastSyncedAt: sync.lastSyncedAt ?? null,
    nextRetryAt: sync.nextRetryAt ?? null,
    queuedCount: sync.queuedCount ?? 0,
  };
}

function resolveAssistantRoute(target) {
  switch (target) {
    case "dashboard":
      return "/dashboard";
    case "history":
      return "/history";
    case "inventory":
      return "/inventory";
    case "sell":
      return "/sell";
    default:
      return null;
  }
}

export default function Assistant() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { language, locale, messages, setLanguage } = useLocalization();
  const copy = messages.assistantPage;
  const common = messages.common;
  const { credits, products, sales, setCreditBalance, refreshCredits, sync, reload } = useWorkspace();
  const quickPrompts = useMemo(() => ([
    copy.promptSoldToday,
    copy.promptLowStock,
    copy.promptDebtOutstanding,
    copy.promptRestockFirst,
    copy.promptPerformingBest,
    copy.promptCompareWeeks,
  ]), [copy]);
  const starterMessages = useMemo(() => ([
    { id: "welcome", role: "assistant", text: copy.welcome },
  ]), [copy]);
  const [messagesList, setMessages] = useState(starterMessages);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [historyCursor, setHistoryCursor] = useState("");
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [sendError, setSendError] = useState("");
  const bottomRef = useRef(null);
  const chatScrollerRef = useRef(null);
  const shouldScrollToBottomRef = useRef(true);
  const availableCreditUnits = Number(credits?.availableUnits || 0);
  const hasAssistantCredits = availableCreditUnits >= ASSISTANT_MESSAGE_COST_UNITS;
  const canUseAssistant = !historyLoading && !historyError;

  useEffect(() => {
    setMessages((current) => {
      if (current.length === 1 && current[0]?.id === "welcome") {
        return starterMessages;
      }
      return current;
    });
  }, [starterMessages]);

  useEffect(() => {
    if (!shouldScrollToBottomRef.current) return;
    shouldScrollToBottomRef.current = false;
    bottomRef.current?.scrollIntoView({ behavior: historyLoading ? "auto" : "smooth" });
  }, [historyLoading, messagesList, thinking]);

  const loadInitialHistory = useCallback(async () => {
    if (!session?.sessionId) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const page = await fetchAssistantMessages(session.sessionId, "", 30);
      const loaded = Array.isArray(page?.messages) ? page.messages : [];
      shouldScrollToBottomRef.current = true;
      setMessages(loaded.length ? loaded : starterMessages);
      setHistoryCursor(page?.nextCursor || "");
      setHasMoreHistory(Boolean(page?.hasMore && page?.nextCursor));
    } catch (error) {
      setMessages([]);
      setHistoryError(sanitizeAssistantText(error?.message || copy.requestFailed));
    } finally {
      setHistoryLoading(false);
    }
  }, [copy.requestFailed, session?.sessionId, starterMessages]);

  useEffect(() => {
    void loadInitialHistory();
  }, [loadInitialHistory]);

  const loadMoreHistory = useCallback(async () => {
    if (!session?.sessionId || !historyCursor || !hasMoreHistory || historyLoadingMore) return;
    const scroller = chatScrollerRef.current;
    const previousHeight = scroller?.scrollHeight || 0;
    setHistoryLoadingMore(true);
    try {
      const page = await fetchAssistantMessages(session.sessionId, historyCursor, 30);
      const older = Array.isArray(page?.messages) ? page.messages : [];
      shouldScrollToBottomRef.current = false;
      setMessages((current) => {
        const currentIDs = new Set(current.map((message) => message.id));
        return [...older.filter((message) => !currentIDs.has(message.id)), ...current];
      });
      setHistoryCursor(page?.nextCursor || "");
      setHasMoreHistory(Boolean(page?.hasMore && page?.nextCursor));
      window.requestAnimationFrame(() => {
        if (scroller) {
          scroller.scrollTop += scroller.scrollHeight - previousHeight;
        }
      });
    } catch (error) {
      setSendError(sanitizeAssistantText(error?.message || copy.requestFailed));
    } finally {
      setHistoryLoadingMore(false);
    }
  }, [copy.requestFailed, hasMoreHistory, historyCursor, historyLoadingMore, session?.sessionId]);

  const clientContext = useMemo(() => ({
    localProducts: selectAssistantLocalProducts(products),
    localRecentSales: selectAssistantRecentSales(sales, common),
    syncSummary: toAssistantSyncSummary(sync),
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    language,
    locale,
  }), [common, language, locale, products, sales, sync]);

  const send = async (text) => {
    const q = (text ?? draft).trim();
    if (!q || !session?.sessionId || thinking || !canUseAssistant) return;
    if (!hasAssistantCredits) {
      notifyInsufficientCredits({
        availableUnits: availableCreditUnits,
        feature: "assistant",
        requiredUnits: ASSISTANT_MESSAGE_COST_UNITS,
      });
      return;
    }

    const timestamp = Date.now();
    const requestId = globalThis.crypto?.randomUUID?.() || `web-${timestamp}-${Math.random().toString(36).slice(2)}`;
    const userMessageId = `${requestId}:pending-user`;
    const assistantMessageId = `${requestId}:pending-assistant`;
    const conversation = messagesList
      .filter((message) => message.text.trim())
      .slice(-ASSISTANT_MESSAGE_CONTEXT_LIMIT)
      .map((message) => ({
        role: message.role,
        text: message.text,
      }));

    setSendError("");
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      { id: userMessageId, role: "user", text: q },
      { id: assistantMessageId, role: "assistant", text: "" },
    ]);
    setDraft("");
    setThinking(true);

    try {
      const response = await fetchAssistantReply(session.sessionId, {
        clientContext,
        messages: [...conversation, { role: "user", text: q }],
        question: q,
        message: q,
        requestId,
      });
      const nextCredits = response?.assistant?.credits ?? null;
      if (nextCredits) {
        setCreditBalance(nextCredits);
      } else {
        refreshCredits().catch(() => null);
      }
      const reply = extractAssistantReply(response);
      const textReply = sanitizeAssistantText(reply?.text || copy.dontKnowYet);
      const persistedMessages = Array.isArray(response?.messages) ? response.messages : [];
      shouldScrollToBottomRef.current = true;
      setMessages((prev) => {
        const withoutPending = prev.filter((message) => message.id !== userMessageId && message.id !== assistantMessageId);
        if (persistedMessages.length) {
          return [...withoutPending, ...persistedMessages];
        }
        return [
          ...withoutPending,
          { id: userMessageId, role: "user", text: q },
          {
            id: assistantMessageId,
            role: "assistant",
            actionLabel: reply?.actionLabel,
            actionTarget: reply?.actionTarget,
            text: textReply,
          },
        ];
      });
    } catch (error) {
      if (error?.status === 402) {
        refreshCredits().catch(() => null);
      }
      setMessages((prev) => prev.filter((message) => message.id !== userMessageId && message.id !== assistantMessageId));
      const isInsufficientCredits = error?.code === "INSUFFICIENT_CREDITS" || (
        error?.status === 402 && String(error?.message || "").toLowerCase().includes("insufficient credits")
      );
      if (!isInsufficientCredits) {
        setSendError(sanitizeAssistantText(error?.message || copy.requestFailed));
      }
    } finally {
      setThinking(false);
    }
  };

  const handleAssistantAction = async (target) => {
    if (target === "sync") {
      await reload();
      return;
    }

    const route = resolveAssistantRoute(target);
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] min-h-[calc(100vh-4rem)]">
      <div className="flex flex-col min-h-[calc(100vh-4rem)]">
        <div className="border-b border-border/80 px-6 py-3 lg:px-8">
          <div className="flex min-h-10 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/[0.08] text-primary ring-1 ring-primary/10">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold leading-4 tracking-tight">{copy.title}</h1>
              <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{copy.subtitle}</p>
            </div>
            <Select value={language} onValueChange={(value) => void setLanguage(value)}>
              <SelectTrigger
                className="ml-auto h-8 w-[132px] rounded-full border-border/70 bg-background px-3 text-[11px] focus:ring-0"
                aria-label={copy.languageLabel}
                data-testid="assistant-language-select"
              >
                <Languages className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languageOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.nativeLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span
              className={`hidden shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] sm:inline-flex ${
                historyError
                  ? "bg-amber-500/[0.08] text-amber-700"
                  : "bg-primary/[0.06] text-primary/80"
              }`}
              aria-live="polite"
            >
              <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                {!historyError ? (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                ) : null}
                <span
                  className={`relative inline-flex h-1.5 w-1.5 rounded-full ${
                    historyError ? "bg-amber-400" : "bg-emerald-500"
                  }`}
                />
              </span>
              {historyError ? copy.reconnecting : historyLoading ? copy.connecting : copy.live}
            </span>
          </div>
        </div>

        <div
          ref={chatScrollerRef}
          onScroll={(event) => {
            if (event.currentTarget.scrollTop <= 80) {
              void loadMoreHistory();
            }
          }}
          className="flex-1 overflow-y-auto px-6 lg:px-8 py-8 max-w-4xl w-full mx-auto"
        >
          <div className="space-y-6">
            {historyLoadingMore ? (
              <div className="text-center text-[11px] text-muted-foreground">{copy.loadingEarlierMessages}</div>
            ) : null}
            {historyError ? (
              <div className="mx-auto flex min-h-[260px] max-w-sm flex-col items-center justify-center text-center">
                <div className="relative mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/[0.07] text-primary">
                  <RefreshCcw className="h-5 w-5" />
                  <span className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-background bg-amber-400" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">{copy.chatReconnecting}</h2>
                <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                  {copy.chatReconnectDescription}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-full px-4"
                  onClick={() => void loadInitialHistory()}
                >
                  <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                  {copy.tryAgain}
                </Button>
              </div>
            ) : (
              messagesList.map((m) => (
                <Message key={m.id} m={m} copy={copy} onAction={handleAssistantAction} />
              ))
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <div className="border-t border-border p-4 bg-background/80 backdrop-blur-xl">
          <div className="max-w-4xl mx-auto">
            {!hasAssistantCredits ? (
              <div className="mb-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div className="min-w-0">
                  <div className="font-semibold">{copy.outOfCreditsTitle}</div>
                  <div className="mt-0.5 text-amber-800/90">{copy.outOfCreditsBody}</div>
                </div>
              </div>
            ) : null}
            {sendError ? (
              <div className="mb-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-xs text-destructive">
                {sendError}
              </div>
            ) : null}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="relative flex items-center gap-2"
            >
              <Input
                data-testid="assistant-input"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={hasAssistantCredits ? copy.inputPlaceholder : copy.outOfCreditsPlaceholder}
                disabled={!canUseAssistant}
                className="h-12 pl-4 pr-12 text-base focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input"
              />
              <Button
                data-testid="assistant-send"
                type="submit"
                size="icon"
                disabled={!draft.trim() || thinking || !canUseAssistant}
                className="h-10 w-10 absolute right-1 top-1"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
              <span>{copy.inputCostHint}</span>
              <span className="text-right">{copy.footer}</span>
            </div>
          </div>
        </div>
      </div>

      <aside className="hidden xl:block border-l border-border p-6 bg-muted/20">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-3">{copy.suggestedPrompts}</div>
        <div className="space-y-2 mb-8">
          {quickPrompts.map((p, i) => (
            <button
              key={i}
              data-testid={`assistant-prompt-${i}`}
              onClick={() => send(p)}
              className="w-full text-left px-3 py-2.5 rounded-md border border-border bg-card hover:border-primary/40 transition-colors text-sm group"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{p}</span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          ))}
        </div>

        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-3">{copy.actions}</div>
        <div className="space-y-2">
          <ActionRow icon={<ReceiptText className="h-4 w-4" />} label={copy.actionNewSale} onClick={() => navigate("/sell")} />
          <ActionRow icon={<Boxes className="h-4 w-4" />} label={copy.actionAddProduct} onClick={() => navigate("/inventory")} />
          <ActionRow icon={<BarChart3 className="h-4 w-4" />} label={copy.actionWeeklyReport} onClick={() => navigate("/analytics")} />
          <ActionRow icon={<RefreshCcw className="h-4 w-4" />} label={copy.actionForceSync} onClick={reload} />
        </div>
      </aside>
    </div>
  );
}

function Message({ m, copy, onAction }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} gap-3`}>
      {!isUser && (
        <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
      )}
      <div className={`max-w-[720px] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"
      }`}>
        <div className="whitespace-pre-wrap">{m.text || copy.thinking}</div>
        {m.actionLabel && m.actionTarget ? (
          <button
            onClick={() => onAction(m.actionTarget)}
            className="mt-3 inline-flex items-center rounded-md bg-background px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-accent"
          >
            {m.actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ActionRow({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-card hover:bg-accent transition-colors text-sm text-left"
    >
      <span className="text-primary">{icon}</span>
      <span className="flex-1">{label}</span>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
  );
}
